import { useState, useEffect, useCallback } from 'react';
import {
  CircularProgress, Button, Typography, Grid, Divider, Box, TextField, Alert, RadioGroup, Radio
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PropTypes from 'prop-types';

import { fNumber } from '../../utils/formatNumber';
import { NAME_REGEX, DESCRIPTION_REGEX } from '../../utils/constants'; 
import axiosInstance from '../../api';
import ColorPicker from '../../components/color-utils/ColorPicker';
import SERVER_API_URL from '../../config';
import Iconify from '../../components/iconify/Iconify';
import { REQUIRED_FIELD, NAME_EXCEPTION, DESCRIPTION_EXCEPTION, INTERNAL_SERVER_ERROR } from '../../utils/exception';
import UpdaterButton from '../common/UpdaterButton';
import RegistererButton from '../common/RegistererButton';
import DateInfo from '../common/DateInfo';
import RegisteredBy from '../common/RegisteredBy';
import UpdaterList from '../common/UpdaterList';
import StyledCardNoHeight from '../../components/card/StyledCardNoHeight';

export default function CategoryUpdateForm({ categoryToBeUpdated, setCategoryToBeUpdated, userId }) {
  const [categoryName, setCategoryName] = useState(categoryToBeUpdated.name);
  const [description, setDescription] = useState(categoryToBeUpdated.description);
  const [color, setColor] = useState(categoryToBeUpdated.color);
  const [confessor, setConfessor] = useState(categoryToBeUpdated.type);

  // console.log('categoryToBeUpdated.type', categoryToBeUpdated.type)

  const [disabled, setDisabled] = useState(false);
  const [firstDisabled, setFirstDisabled] = useState(true);
  const categoryColor = categoryToBeUpdated.color.toString();
  const [registeredBy, setRegisteredBy] = useState(null);
  const [openUpdater, setOpenUpdater] = useState(null);
  const [updaterList, setUpdaterList] = useState([]);
  const [openRegisterer, setOpenRegisterer] = useState(null);

  const handleRegistererMenu = (event) => {
    setOpenRegisterer(event.currentTarget);
  };

  const handleUpdaterMenu = (event) => {
    setOpenUpdater(event.currentTarget);
  };

  const handleCloseMenu = async () => {
    setOpenRegisterer(null);
    setOpenUpdater(null);
  };

  const getRegisteredBy = useCallback(async () => {
    try {
      const response = await axiosInstance.post(
        `${SERVER_API_URL}/category/findRegisteredBy/${categoryToBeUpdated.id}`
      );
      if (response.status === 200) {
        setRegisteredBy(response.data);
      }
    } catch (error) {
      // console.log(error)
    }
  }, [categoryToBeUpdated]);

  const getUpdaterList = useCallback(async () => {
    try {
      const response = await axiosInstance.post(`${SERVER_API_URL}/category/findUpdaterList/${categoryToBeUpdated.id}`);
      if (response.status === 200) {
        setUpdaterList(response.data);
      }
    } catch (error) {
      // console.log(error)
    }
  }, [categoryToBeUpdated]);

  useEffect(() => {
    getRegisteredBy();
    getUpdaterList();
  }, [getRegisteredBy, getUpdaterList]);

  const handleSubmitNewCategory = async (event) => {
    setDisabled(true);
    event.preventDefault();
    setFormErrorSeverity('error');

    if (!formErrorOnCategoryName && !formErrorOnDescription) {
      try {
        const response = await axiosInstance.put(
          `${SERVER_API_URL}/category/updateOne/${categoryToBeUpdated.id}/${userId}`,
          {
            categoryName,
            description,
            color,
            type: confessor,
          }
        );

        if (response.status === 200) {
          setFormError('ምድብ በተሳካ ሁኔታ ተስተካክሏል።');
          setFormErrorSeverity('success');
          setCategoryToBeUpdated(response.data);
          getUpdaterList();
        }
      } catch (e) {
        if (e.response.status === 406) {
          setFormError('የምድብ ስም አስቀድሞ ተወስዷል!');
        } else if (e.response.status === 500 || e.response.status === 400) {
          setFormError(INTERNAL_SERVER_ERROR);
        }
      }
    }
    setDisabled(false);
    setFirstDisabled(true);
  };

  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const [categoryNameClicked, setCategoryNameClicked] = useState(false);
  const [formErrorOnCategoryName, setFormErrorOnCategoryName] = useState(false);
  const [helperTextOnCategoryName, setHelperTextOnCategoryName] = useState(null);

  const [descriptionClicked, setDescriptionClicked] = useState(false);
  const [formErrorOnDescription, setFormErrorOnDescription] = useState(false);
  const [helperTextOnDescription, setHelperTextOnDescription] = useState(null);

  const [confessorClicked, setConfessorClicked] = useState(false);
  const [formErrorOnConfessor, setFormErrorOnConfessor] = useState(false);
  const [helperTextOnConfessor, setHelperTextOnConfessor] = useState(null);

  const onColorChange = (newColor) => {
    setFirstDisabled(false);
    setColor(newColor);
  };

  const handleFormValueBlur = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');

    const { name, value } = e.target;
    switch (name) {
      case 'categoryName':
        if (value.length === 0) {
          setCategoryNameClicked(false);
          setFormErrorOnCategoryName(categoryNameClicked && value.length === 0);
          setHelperTextOnCategoryName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnCategoryName(true);
          setHelperTextOnCategoryName(NAME_EXCEPTION);
        } else {
          setHelperTextOnCategoryName(null);
          setFormErrorOnCategoryName(false);
        }
        break;
      case 'description':
        if (value.length === 0) {
          setDescriptionClicked(false);
          setFormErrorOnDescription(descriptionClicked && value.length === 0);
          setHelperTextOnDescription(REQUIRED_FIELD);
        } else if (!DESCRIPTION_REGEX.test(value)) {
          setFormErrorOnDescription(true);
          setHelperTextOnDescription(DESCRIPTION_EXCEPTION);
        } else {
          setHelperTextOnDescription(null);
          setFormErrorOnDescription(false);
        }
        break;
      case 'confessor':
        if (value.length === 0) {
          setConfessorClicked(false);
          setFormErrorOnConfessor(confessorClicked && value.length === 0);
          setHelperTextOnConfessor(REQUIRED_FIELD);
        } else {
          setHelperTextOnConfessor(null);
          setFormErrorOnConfessor(false);
        }
        break;
      default:
        break;
    }
  };

  const handleFormValueChange = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');
    setFirstDisabled(false);

    const { name, value } = e.target;
    switch (name) {
      case 'categoryName':
        setCategoryName(value);
        if (NAME_REGEX.test(value)) {
          setFormErrorOnCategoryName(false);
          setHelperTextOnCategoryName(null);
        } else {
          setFormErrorOnCategoryName(false);
          setHelperTextOnCategoryName(null);
        }
        break;
      case 'description':
        setDescription(value);
        if (DESCRIPTION_REGEX.test(value)) {
          setFormErrorOnDescription(false);
          setHelperTextOnDescription(null);
        } else {
          setFormErrorOnDescription(false);
          setHelperTextOnDescription(null);
        }
        break;
      case 'confessor':
        setConfessor(value);
        if (DESCRIPTION_REGEX.test(value)) {
          setFormErrorOnConfessor(false);
          setHelperTextOnConfessor(null);
        } else {
          setFormErrorOnConfessor(false);
          setHelperTextOnConfessor(null);
        }
        break;
      default:
        break;
    }
  };

  const handleClearForm = () => {
    setCategoryName(categoryToBeUpdated.name);
    setDescription(categoryToBeUpdated.description);
    setColor(categoryToBeUpdated.color);
    setConfessor(categoryToBeUpdated.type);

    setFormError(null);
    setFormErrorSeverity('error');

    setCategoryNameClicked(false);
    setFormErrorOnCategoryName(false);
    setHelperTextOnCategoryName(null);

    setDescriptionClicked(false);
    setFormErrorOnDescription(false);
    setHelperTextOnDescription(null);
  };

  return (
    <StyledCardNoHeight
      sx={{ padding: { xs: 1, sm: 10 } }}
      component="form"
      autoComplete="off"
      onSubmit={handleSubmitNewCategory}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={7}>
          <Typography variant="h5" sx={{ color: 'text.secondary' }}>
            <Iconify sx={{ width: 30, height: 30, color: categoryColor }} icon="ic:twotone-family-restroom" />
            {categoryToBeUpdated.name.toUpperCase()} ምድብ
          </Typography>
          <Typography sx={{ ml: 5, color: 'darkgray' }}>
            {fNumber(categoryToBeUpdated && categoryToBeUpdated.usersCount)} : ተጠቃሚዎች
          </Typography>
        </Grid>
        <Grid item xs={12} sm={5}>
          <Box sx={{ textAlign: 'right' }}>
            {categoryToBeUpdated && (
              <DateInfo createdAt={categoryToBeUpdated.createdAt} updatedAt={categoryToBeUpdated.updatedAt} />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'right', gap: 1 }}>
              {updaterList && <UpdaterButton updaterList={updaterList} handleUpdaterMenu={handleUpdaterMenu} />} :{' '}
              {registeredBy && (
                <RegistererButton registeredBy={registeredBy} handleRegistererMenu={handleRegistererMenu} />
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Divider sx={{ borderStyle: 'dashed' }} />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          {formError && (
            <Alert sx={{ width: '100%' }} severity={formErrorSeverity}>
              {formError}
            </Alert>
          )}
        </Grid>
        <Grid item xs={12} sm={9}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                error={formErrorOnCategoryName}
                id="categoryName"
                name="categoryName"
                label="የምድብ ስም"
                value={categoryName}
                fullWidth
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnCategoryName ? helperTextOnCategoryName : null}
                onFocus={() => setCategoryNameClicked(true)}
                onBlur={handleFormValueBlur}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RadioGroup
                required
                error={formErrorOnConfessor}
                name="confessor"
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnConfessor ? helperTextOnConfessor : null}
                onFocus={() => setConfessorClicked(true)}
                onBlur={handleFormValueBlur}
                value={confessor}
              >
                <Box sx={{ display: 'flex', mt: 1 }}>
                  <Typography color='text.secondary' sx={{ mr: 1 }}>የንስሃ አባት መሆን: </Typography>
                  <Box sx={{ display: 'flex' }}>
                    <Radio defaultChecked={confessor === "confessor"} value="confessor" sx={{ mt: -1 }} /> ይችላል
                    <Radio defaultChecked={confessor === "nonConfessor"} value="nonConfessor" sx={{ mt: -1 }} /> አይችልም
                  </Box>
                </Box>
              </RadioGroup>
            </Grid>
            <Grid item xs={12} sm={12} sx={{ mt: 2 }}>
              <TextField
                required
                error={formErrorOnDescription}
                id="description"
                name="description"
                label="የምድብ መግለጫ"
                value={description}
                fullWidth
                multiline
                rows={5}
                onChange={handleFormValueChange}
                helperText={formErrorOnDescription ? helperTextOnDescription : null}
                onFocus={() => setDescriptionClicked(true)}
                onBlur={handleFormValueBlur}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={3}>
          <ColorPicker color={color} setColor={(newColor) => onColorChange(newColor)} />
        </Grid>
        <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button color="error" type="reset" onClick={handleClearForm} sx={{ minWidth: { xs: 80, sm: 150 } }}>
            <RefreshIcon /> ቅጽ ዳግም አስጀምር
          </Button>
          <Button
            variant="contained"
            type="submit"
            sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, sm: 150 } }}
            disabled={firstDisabled}
          >
            {disabled && <CircularProgress style={{ width: '20px', height: '20px' }} />} ቅጽ አስገባ
          </Button>
        </Grid>
      </Grid>

      {registeredBy && (
        <RegisteredBy openRegister={openRegisterer} registeredBy={registeredBy} handleCloseMenu={handleCloseMenu} />
      )}
      {updaterList && (
        <UpdaterList openUpdater={openUpdater} updaterList={updaterList} handleCloseMenu={handleCloseMenu} />
      )}
    </StyledCardNoHeight>
  );
}

CategoryUpdateForm.propTypes = {
  categoryToBeUpdated: PropTypes.object.isRequired,
  setCategoryToBeUpdated: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
};
